import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, Form, Button, Alert, Col, Spinner, ProgressBar, Modal, Table, Badge, Row } from "react-bootstrap";
import { CONFIG, VALIDATION } from "../utils/config";
import { generateCsvTemplate } from "../utils/helpers";
import { ResultModal } from "../components/CommonUI";

function TemplateInterface({ templateId, onBack, addLog, saveTransaction, notify }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // MODES: 'single' | 'bulk' | 'multi' (Group Single Loop)
  const [mode, setMode] = useState("single");

  // Single Mode State
  const [target, setTarget] = useState("");
  const [vars, setVars] = useState({});
  const [sending, setSending] = useState(false);

  // Scheduler State
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduleTime, setScheduleTime] = useState("");

  // Bulk CSV Mode State
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [batchId, setBatchId] = useState(null);

  // Generic Status Modal (for Bulk Batch OR Single Message)
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  // --- Multi-Target (Group Manual) State ---
  const [multiText, setMultiText] = useState("");
  const [multiLogs, setMultiLogs] = useState([]);
  const [isMultiProcessing, setIsMultiProcessing] = useState(false);
  const [multiProgress, setMultiProgress] = useState(0);
  const [useSharedVars, setUseSharedVars] = useState(true);
  const stopMultiRef = useRef(false);

  // Processing "Cancel" state for individual buttons
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        addLog(`Fetching metadata for Template: ${templateId}...`);
        const res = await axios.post(`${CONFIG.API_BASE}/templates`, {
          Content_Template_Id: templateId,
          Content_Template_Name: ""
        });

        if (res.data && res.data.Content_Template_Ids && res.data.Content_Template_Ids.length > 0) {
          setDetails(res.data.Content_Template_Ids[0]);
          addLog("Template metadata loaded successfully.", "SUCCESS");
        } else {
          addLog("Template not found on BSNL server. Using generic fallback.", "WARN");
          setDetails({
            Template_Name: "Generic Template (Manual Mode)",
            Template_Message: "Content unavailable. Ensure variables match DLT.",
            Template_Keys: ["var1", "var2"]
          });
        }
      } catch (e) {
        addLog(`Metadata Fetch Error: ${e.message}`, "ERROR");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [templateId, addLog]);

  useEffect(() => {
    if (mode === 'multi') {
      const firstLine = multiText.trim().split('\n')[0] || "";
      if (firstLine.toLowerCase().includes("target") && firstLine.includes(",")) {
        setUseSharedVars(false);
      } else {
        setUseSharedVars(true);
      }
    }
  }, [multiText, mode]);

const getFormattedDate = () => {
    if (!isScheduled) return "";

    if (!scheduleTime) {
      alert("Please select date & time");
      return null;
    }

    const d = new Date(scheduleTime);
    const now = new Date();

    // Minimum 15 minutes rule (BSNL usually allows 15-20 mins)
    if (d.getTime() < now.getTime() + 15 * 60000) {
      alert("BSNL requires at least 15-20 minutes lead time");
      return null;
    }

    // 🔥 FIX: Increased limit from 23 hours to 7 Days
    // Previous code: if (d.getTime() > now.getTime() + 23 * 60 * 60000)
    if (d.getTime() > now.getTime() + 7 * 24 * 60 * 60000) {
      alert("Scheduling is limited to 7 days in advance.");
      return null;
    }

    const pad = n => String(n).padStart(2, "0");

    // Format: DD-MM-YYYY HH:mm:ss
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };


  // --- SINGLE SMS LOGIC ---
// --- SINGLE SMS LOGIC ---
  const sendSingle = async () => {
    if (!VALIDATION.MOBILE.test(target)) return alert("Invalid Mobile Number");

    // 1. Handle Date Logic
    const formattedDate = getFormattedDate();
    // If scheduled is ON but date is invalid, stop.
    if (isScheduled && !formattedDate) return;

    setSending(true);

    const kv = Object.keys(vars).map(k => ({ Key: k, Value: vars[k] }));

    const payload = {
      Target: target,
      Content_Template_Id: templateId,
      Template_Keys_and_Values: kv
    };

    if (isScheduled && formattedDate) {
      payload.Scheduled_Time = formattedDate;
    }

    const endpoint = isScheduled ? 'schedule-sms' : 'send-sms';
    const logType = isScheduled ? 'Scheduled (Single)' : 'Single';

    try {
      addLog(`Sending ${logType} SMS...`, "INFO");
      const res = await axios.post(`${CONFIG.API_BASE}/${endpoint}`, payload);

      const msgId = res.data?.Message_Id || res.data?.MessageId;
      const status = msgId
        ? (isScheduled ? "Scheduled" : "Success")
        : "Failed";

      // SAVE booking row in SQLite
      if (msgId) {
        try {
          // 🔥 FIX: Calculate the correct time to save in DB
          // If Scheduled: Use the selected 'scheduleTime'. If Instant: Use 'new Date()'
          const recordedTime = (isScheduled && scheduleTime) 
            ? new Date(scheduleTime).toISOString() 
            : new Date().toISOString();

          await axios.post(`${CONFIG.DB_BASE}/batch-details`, [{
            BatchId: msgId,
            Mobile: target,
            MessageId: msgId,
            Status: status,
            SentTime: recordedTime // <--- UPDATED THIS LINE
          }]);

        } catch (dbErr) {
          console.error("Failed to save single SMS tracking:", dbErr);
        }
      }

      saveTransaction(logType, target, templateId, status, res.data, payload);
      setResult({ status, data: res.data });

      if (status === "Success" || status === "Scheduled") {
        setTarget("");
        notify(isScheduled ? "SMS Scheduled Successfully!" : "SMS Sent Successfully!");
      }
    } catch (e) {
      addLog(e.message, "ERROR");
      saveTransaction(logType, target, templateId, "Error", e.message, payload);
      setResult({ status: "Error", data: { Error: e.message } });
    } finally {
      setSending(false);
    }
  };

  // --- GROUP MANUAL: FILE IMPORT HANDLER ---
  const handleGroupFileImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      if (text.toLowerCase().includes("target") && text.includes(",")) {
        setMultiText(text);
        setUseSharedVars(false);
        notify("CSV format detected. Switching to Unique Variable Mode.", "info");
      } else {
        const found = text.match(/[0-9]{10}/g);
        if (found && found.length > 0) {
          const unique = [...new Set(found)];
          setMultiText(unique.join('\n'));
          setUseSharedVars(true);
          notify(`Imported ${unique.length} numbers. Using Shared Variables.`, "success");
        } else {
          alert("No valid numbers or CSV headers found.");
        }
      }
    };
    reader.readAsText(file);
    e.target.value = null;
  };

  // --- GROUP MANUAL: SEND LOGIC ---
 // --- GROUP MANUAL: SEND LOGIC ---
  const handleMultiSend = async () => {
    let processData = [];

    // ... (Data Preparation Logic remains the same) ...
    // [Keep the logic that populates processData]

    if (useSharedVars) {
      const rawNumbers = multiText.split(/[\n,]+/).map(t => t.trim().replace(/\D/g, ''));
      const validNumbers = [...new Set(rawNumbers.filter(n => n.length === 10))];
      if (validNumbers.length === 0) return alert("No valid 10-digit mobile numbers found.");
      processData = validNumbers.map(num => ({
        Target: num,
        Variables: Object.keys(vars).map(k => ({ Key: k, Value: vars[k] }))
      }));
    } else {
       // ... (Keep existing CSV parsing logic) ...
       // (Shortened for brevity - do not remove your existing CSV parsing code)
      const rows = multiText.trim().split('\n');
      if (rows.length < 2) return alert("CSV is empty or missing data rows.");
      const headers = rows[0].split(',').map(h => h.trim());
      for (let i = 1; i < rows.length; i++) {
        // ... (Keep existing CSV row parsing) ...
        // Ensure processData is populated
        const cols = rows[i].split(',');
        if (cols.length === headers.length) {
          let rowObj = {};
          headers.forEach((h, index) => { rowObj[h] = cols[index].trim(); });
          const t = rowObj['Target']?.replace(/\D/g, '');
          if (t && t.length === 10) {
             const rowVars = details?.Template_Keys.map(k => ({ Key: k, Value: rowObj[k] || "" }));
             processData.push({ Target: t, Variables: rowVars });
          }
        }
      }
    }

    if (!window.confirm(`Ready to send to ${processData.length} unique numbers?`)) return;

    const formattedDate = getFormattedDate();
    if (isScheduled && !formattedDate) return;

    setIsMultiProcessing(true);
    stopMultiRef.current = false;
    setMultiLogs([]);
    setMultiProgress(0);

    // [FIX 1] Generate a Common Batch ID for this Group
    const clientBatchId = "GRP-" + Math.random().toString(36).substr(2, 9).toUpperCase();
    
    const endpoint = isScheduled ? 'schedule-sms' : 'send-sms';
    const logType = isScheduled ? 'Group (Sched)' : 'Group (Client)';

    let aggregatedLogs = [];
    let successCount = 0;
    let failCount = 0;
    let bsnlBatchId = null;

    // Loop through numbers
    for (let i = 0; i < processData.length; i++) {
      if (stopMultiRef.current) break;

      const item = processData[i];

      // [FIX 2] Pass 'Batch_Id' in the payload
      const payload = {
        Target: item.Target,
        Content_Template_Id: templateId,
        Template_Keys_and_Values: item.Variables,
        Batch_Id: clientBatchId // <--- CRITICAL: Forces Backend to use this ID
      };

      if (isScheduled && formattedDate) {
        payload.Scheduled_Time = formattedDate;
      }

      // 🔥 UPDATED: Show "Booking Time" (Scheduled Time) if scheduled, else Current Time
      const displayTime = (isScheduled && formattedDate) 
        ? formattedDate 
        : new Date().toLocaleTimeString();

      let logEntry = {
        mobile: item.Target,
        status: "Pending",
        details: "Sending...",
        messageId: null,
        time: displayTime // <--- UPDATED: Uses Booking Time if scheduled
      };

      try {
        const res = await axios.post(`${CONFIG.API_BASE}/${endpoint}`, payload);
        const apiMsgId = res.data.Message_Id || res.data.MessageId;

        // Capture BSNL batch id if available (for reference only)
        if (isScheduled && apiMsgId && !bsnlBatchId) {
          bsnlBatchId = apiMsgId;
        }

        const isSuccess = Boolean(apiMsgId);

        if (isSuccess) {
          logEntry.status = isScheduled ? "Scheduled" : "Success";
          logEntry.messageId = apiMsgId;
          logEntry.details = isScheduled ? "Scheduled" : "Sent";
          successCount++;
        } else {
          logEntry.status = "Failed";
          logEntry.details = res.data.Error ?? "Accepted by BSNL";
          failCount++;
        }

      } catch (e) {
        logEntry.status = "Error";
        logEntry.details = e.message;
        failCount++;
      }

      aggregatedLogs.push(logEntry);
      setMultiLogs(prev => [...prev, logEntry]);

      // [FIX 3] REMOVED 'sqlLogs.push' logic here. 
      // The Backend (BsnlController) now saves the record with the passed Batch_Id.
      // Saving it again here would create duplicates.

      setMultiProgress(Math.round(((i + 1) / processData.length) * 100));
      await new Promise(r => setTimeout(r, 100));
    }

    // [FIX 4] REMOVED the 'axios.post(.../db/batch-details)' call.
    // The backend handles data persistence now.

    const summary = `Batch of ${aggregatedLogs.length} | S:${successCount} F:${failCount}`;
    
    // Save the summary transaction (Parent Record)
    saveTransaction(
      logType,
      `${processData.length} Numbers`,
      templateId,
      isScheduled ? "Scheduled" : (successCount > 0 ? "Success" : "Failed"),
      {
        api_response: JSON.stringify({
          BatchId: clientBatchId, // Use the client batch ID for grouping reference
          batch_summary: summary,
          individual_logs: aggregatedLogs
        }),
        request_payload: JSON.stringify({
          Scheduled_Time: formattedDate,
          Mode: useSharedVars ? "Shared Vars" : "CSV Mode",
          Batch_Id: clientBatchId
        })
      },
      null
    );

    setIsMultiProcessing(false);
    notify(`Batch Finished: ${successCount} Sent, ${failCount} Failed.`);
  };

  const downloadMultiReport = () => {
    if (multiLogs.length === 0) return alert("No data to export");
    const headers = ["Mobile", "Time", "Status", "Message ID", "Response"];
    const rows = multiLogs.map(l => [l.mobile, l.time, l.status, l.messageId, l.details]);
    const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `group_sms_report_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
  };

  // --- STATUS TRACKING (Generic) ---
  const checkStatus = async (id, isBatch) => {
    setStatusLoading(true);
    setShowStatusModal(true);
    setStatusData(null);

    try {
      const endpoint = isBatch ? 'check-bulk-status' : 'check-status';
      // API Doc: use 'Message_id' (lowercase 'id') for individual tracking
      const payload = isBatch ? { Batch_Id: id } : { Message_id: id };

      // 1. Call BSNL API (via your Proxy)
      const res = await axios.post(`${CONFIG.API_BASE}/${endpoint}`, payload);
      setStatusData(res.data);

      // 2. IF NOT BATCH: Update Local SQLite Database with new Status
      if (res.data && !res.data.Error) {

        await axios.put(`${CONFIG.API_BASE}/db/update-status`, {
          MessageId: id,
          NewStatus:
            res.data.Message_Status_Description ||
            res.data.Delivery_Status ||
            res.data.Status ||
            "Checked",
          ResponseDetails: JSON.stringify(res.data)
        });


        // Determine status string based on BSNL response code/text
        const newStatus = res.data.Delivery_Status || res.data.Status || "Checked";

        try {
          await axios.put(`${CONFIG.API_BASE}/db/update-status`, {
            MessageId: id,
            NewStatus: newStatus,
            ResponseDetails: JSON.stringify(res.data)
          });
          console.log("Local DB Updated with new status");
        } catch (dbErr) {
          console.error("Failed to update local DB:", dbErr);
        }
      }

    } catch (e) {
      setStatusData({ Error: e.message });
    } finally {
      setStatusLoading(false);
    }
  };

  // --- NEW: HANDLE SINGLE CANCEL IN LIST ---
  const handleCancelSingle = async (msgId) => {
    if (!msgId) return;
    if (!window.confirm("Cancel this scheduled message?")) return;

    setCancellingId(msgId);
    try {
      const res = await axios.post(`${CONFIG.API_BASE}/cancel-schedule`, { Message_Id: msgId });

      if (res.data && res.data.Success) {
        notify("Message Cancelled Successfully!");
        // Update local state to reflect change instantly
        setMultiLogs(prev => prev.map(log => {
          if (log.messageId === msgId) {
            return { ...log, status: "Cancelled", details: "Cancelled by User" };
          }
          return log;
        }));
      } else {
        alert("Cancel Failed: " + (res.data.Error || "Unknown Error"));
      }
    } catch (e) {
      alert("Cancel Error: " + e.message);
    } finally {
      setCancellingId(null);
    }
  };

  // --- BULK API LOGIC ---
  const handleBulkUpload = async () => {
    if (!file) return alert("Please select a valid CSV file");
    const formattedDate = getFormattedDate();
    if (isScheduled && !formattedDate) return;

    const isValid = await validateCsvFile(file);
    if (!isValid) return;

    setUploading(true);
    setProgress(0);
    setBatchId(null);

    const fd = new FormData();
    fd.append("file", file);

    // Only append Scheduled_Time if it exists
    if (isScheduled && formattedDate) {
      fd.append("Scheduled_Time", formattedDate);
    }

    const logType = isScheduled ? 'Scheduled (Bulk)' : 'Bulk';

    try {
      addLog(`Starting ${logType} Upload: ${file.name}`);
      const res = await axios.post(`${CONFIG.API_BASE}/bulk-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => setProgress(Math.round((ev.loaded * 100) / ev.total))
      });

      addLog("Bulk Process Completed.", "SUCCESS");

      if (res.data && res.data.Batch_Id) {
        setBatchId(res.data.Batch_Id);
        addLog(`Batch ID Received: ${res.data.Batch_Id}`, "INFO");
      }

      saveTransaction(logType, "CSV File", templateId, "Success", res.data, { filename: file.name, Scheduled_Time: formattedDate });
      setResult({ status: "Success", data: res.data });
      notify("File Processed Successfully!");
    } catch (e) {
      addLog(`Upload Failed: ${e.message}`, "ERROR");
      saveTransaction(logType, "CSV File", templateId, "Error", e.message, { filename: file.name, Scheduled_Time: formattedDate });
      setResult({ status: "Error", data: { Error: e.message } });
    } finally {
      setUploading(false);
    }
  };

  const validateCsvFile = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        if (VALIDATION.SCIENTIFIC_NOTATION.test(text)) {
          alert("CRITICAL ERROR: Scientific Notation detected in CSV.");
          resolve(false); return;
        }
        if (!text.includes("Target") || !text.includes("Content_Template_Id")) {
          alert("Invalid CSV Headers.");
          resolve(false); return;
        }
        resolve(true);
      };
      reader.readAsText(file);
    });
  };

  if (loading) return <div className="p-5 text-center"><Spinner animation="border" /> <p className="mt-2">Syncing with BSNL...</p></div>;
  const keys = details?.Template_Keys || [];

  return (
    <div className="fade-in">
      <div className="d-flex align-items-center mb-4">
        <Button variant="outline-dark" size="sm" className="me-3" onClick={onBack}><i className="bi bi-arrow-left"></i> Back</Button>
        <h5 className="m-0 fw-bold text-primary">Send Message: <span className="text-dark">{details?.Template_Name}</span></h5>
      </div>

      <Alert variant="info" className="shadow-sm border-0 d-flex align-items-center">
        <i className="bi bi-info-circle-fill fs-4 me-3"></i>
        <div><strong>Message Content:</strong> <span className="font-monospace small ms-2">{details?.Template_Message}</span></div>
      </Alert>

      {/* --- MODE SELECTION --- */}
      <div className="d-flex justify-content-center mb-4">
        <div className="btn-group shadow-sm">
          <Button variant={mode === 'single' ? 'primary' : 'outline-primary'} onClick={() => setMode('single')} className="px-3 fw-bold"><i className="bi bi-person me-2"></i> Single SMS</Button>
          <Button variant={mode === 'multi' ? 'primary' : 'outline-primary'} onClick={() => setMode('multi')} className="px-3 fw-bold"><i className="bi bi-collection me-2"></i> Group SMS</Button>
          <Button variant={mode === 'bulk' ? 'primary' : 'outline-primary'} onClick={() => setMode('bulk')} className="px-3 fw-bold"><i className="bi bi-file-earmark-spreadsheet me-2"></i> Bulk SMS</Button>
        </div>
      </div>

      <div className="bg-white p-3 rounded mb-3 border border-warning shadow-sm">
        <Form.Check type="switch" id="schedule-switch" label={<span className="fw-bold text-warning">Schedule this batch/message?</span>} checked={isScheduled} onChange={(e) => setIsScheduled(e.target.checked)} />
        {isScheduled && (
          <div className="mt-3 animate-fade-in">
            <Form.Label className="small fw-bold">Select Date & Time</Form.Label>
            <Form.Control type="datetime-local" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} />
            <Form.Text className="text-muted"><i className="bi bi-clock me-1"></i>Select a time at least 20 mins in the future.</Form.Text>
          </div>
        )}
      </div>

      {/* --- 1. SINGLE MODE --- */}
      {mode === 'single' && (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-4">
            <h6 className="fw-bold border-bottom pb-2 mb-3">Recipient Details</h6>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">Mobile Number <span className="text-danger">*</span></Form.Label>
              <Form.Control placeholder="10-digit mobile number" value={target} maxLength={10} onChange={e => setTarget(e.target.value.replace(/\D/g, ''))} />
            </Form.Group>

            {keys.length > 0 && (
              <div className="bg-light p-3 rounded mb-3 border">
                <h6 className="small fw-bold text-muted mb-3">TEMPLATE VARIABLES</h6>
                {keys.map(k => (
                  <Form.Group key={k} className="mb-2 row align-items-center">
                    <Form.Label column sm={3} className="small text-end fw-bold">{k}:</Form.Label>
                    <Col sm={9}><Form.Control size="sm" placeholder={`Value for ${k}`} onChange={e => setVars({ ...vars, [k]: e.target.value })} /></Col>
                  </Form.Group>
                ))}
              </div>
            )}

            <Button variant={isScheduled ? "warning" : "success"} className="w-100 fw-bold py-2 text-white" onClick={sendSingle} disabled={sending}>
              {sending ? <Spinner size="sm" animation="border" className="me-2" /> : (isScheduled ? "SCHEDULE SINGLE SMS" : "SEND SINGLE SMS")}
            </Button>
          </Card.Body>
        </Card>
      )}

      {/* --- 2. MULTI (GROUP) MODE --- */}
      {mode === 'multi' && (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold m-0"><i className="bi bi-collection me-2"></i>Group SMS (Single API Loop)</h6>
              <div className="d-flex gap-2">
                <Button size="sm" variant="outline-dark" onClick={() => generateCsvTemplate(templateId, keys)}>
                  <i className="bi bi-download me-1"></i>Download Template
                </Button>
                {multiLogs.length > 0 && !isMultiProcessing && (
                  <Button size="sm" variant="outline-success" onClick={downloadMultiReport}><i className="bi bi-file-earmark-spreadsheet me-1"></i>Save Report</Button>
                )}
              </div>
            </div>

            <Alert variant={useSharedVars ? "secondary" : "info"} className="small py-2 transition-alert">
              {useSharedVars
                ? <><i className="bi bi-person-lines-fill me-2"></i><strong>Shared Mode:</strong> Entering just numbers will use the "Shared Variables" below for everyone.</>
                : <><i className="bi bi-file-earmark-spreadsheet-fill me-2"></i><strong>CSV Mode:</strong> Detected headers. Each number will use its own variables from the list.</>
              }
            </Alert>

            <Row className="g-3 mb-3">
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="small fw-bold">Manual Input / Imported List</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    placeholder="Paste Numbers OR CSV (Target, var1...)"
                    value={multiText}
                    onChange={e => setMultiText(e.target.value)}
                    disabled={isMultiProcessing}
                    className={useSharedVars ? "" : "font-monospace bg-light"}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Card className="h-100 bg-light border-dashed">
                  <Card.Body className="d-flex flex-column justify-content-center text-center">
                    <h6 className="small fw-bold text-muted">Import from File</h6>
                    <p className="text-muted small mb-2" style={{ fontSize: '0.75rem' }}>Supports CSV (with vars) or Text (numbers only).</p>
                    <Form.Control type="file" size="sm" accept=".csv, .txt" onChange={handleGroupFileImport} disabled={isMultiProcessing} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Variable Inputs (Shared) - Only shown if NOT in CSV mode */}
            {keys.length > 0 && useSharedVars && (
              <div className="bg-light p-3 rounded mb-3 border animate-fade-in">
                <h6 className="small fw-bold text-muted mb-3">SHARED VARIABLES (Applied to All)</h6>
                {keys.map(k => (
                  <Form.Group key={k} className="mb-2 row align-items-center">
                    <Form.Label column sm={3} className="small text-end fw-bold">{k}:</Form.Label>
                    <Col sm={9}><Form.Control size="sm" placeholder={`Value for ${k}`} onChange={e => setVars({ ...vars, [k]: e.target.value })} disabled={isMultiProcessing} /></Col>
                  </Form.Group>
                ))}
              </div>
            )}

            {isMultiProcessing ? (
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="small fw-bold text-primary">Sending Batch...</span>
                  <span className="small fw-bold">{multiProgress}%</span>
                </div>
                <ProgressBar animated now={multiProgress} variant="primary" className="mb-2" />
                <Button variant="danger" size="sm" className="w-100" onClick={() => stopMultiRef.current = true}>STOP PROCESSING</Button>
              </div>
            ) : (
              <Button variant={isScheduled ? "warning" : "primary"} className="w-100 fw-bold py-2 text-white" onClick={handleMultiSend}>
                {isScheduled ? "SCHEDULE FOR GROUP" : "SEND TO GROUP"}
              </Button>
            )}

            {/* Real-time Report Table with Tracking and Time */}
            {multiLogs.length > 0 && (
              <div className="mt-4 border rounded overflow-hidden">
                <div className="bg-light p-2 border-bottom fw-bold small d-flex justify-content-between">
                  <span>Batch Results</span>
                  <span className="badge bg-secondary">{multiLogs.length} Processed</span>
                </div>
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  <Table size="sm" striped hover className="m-0 small align-middle">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>Mobile</th>
                        <th>Time</th>
                        <th>Status</th>
                        <th>Message ID</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {multiLogs.map((log, idx) => (
                        <tr key={idx}>
                          <td>{log.mobile}</td>
                          <td className="text-muted">{log.time}</td>
                          <td>
                            <Badge bg={
                              log.status === "Success" ? "success" :
                                log.status === "Scheduled" ? "warning" :
                                  log.status === "Cancelled" ? "secondary" : "danger"
                            }>
                              {log.status}
                            </Badge>
                          </td>
                          <td className="font-monospace text-muted" style={{ fontSize: '0.8rem' }}>
                            {log.messageId || "N/A"}
                          </td>
                          <td className="text-end">
                            {/* CONDITIONAL ACTION BUTTON: Cancel vs Track */}
                            {log.status === "Scheduled" && log.messageId ? (
                              <Button
                                size="sm"
                                variant="outline-danger"
                                style={{ padding: '2px 8px', fontSize: '0.7rem' }}
                                onClick={() => handleCancelSingle(log.messageId)}
                                disabled={cancellingId === log.messageId}
                              >
                                {cancellingId === log.messageId ? <Spinner size="sm" animation="border" /> : "Cancel"}
                              </Button>
                            ) : log.status === "Cancelled" ? (
                              <Badge bg="secondary">Cancelled</Badge>
                            ) : log.messageId && (
                              <Button
                                size="sm"
                                variant="outline-primary"
                                style={{ padding: '2px 8px', fontSize: '0.7rem' }}
                                onClick={() => checkStatus(log.messageId, false)}
                              >
                                Track
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* --- 3. BULK CSV MODE --- */}
      {mode === 'bulk' && (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
              <h6 className="fw-bold m-0">Bulk Processing (Server-Side CSV)</h6>
              <div className="d-flex gap-2">
                {batchId && (
                  <Button size="sm" variant="info" onClick={() => checkStatus(batchId, true)} disabled={statusLoading}>
                    {statusLoading ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-eye me-1"></i>Track Batch</>}
                  </Button>
                )}
                <Button size="sm" variant="outline-primary" onClick={() => generateCsvTemplate(templateId, keys)}>
                  <i className="bi bi-file-earmark-spreadsheet me-2"></i>Download Template
                </Button>
              </div>
            </div>
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold small">Upload CSV File</Form.Label>
              <Form.Control type="file" accept=".csv" onChange={e => setFile(e.target.files[0])} />
            </Form.Group>
            {uploading && <ProgressBar now={progress} animated striped variant="primary" style={{ height: "10px" }} className="mb-3" />}
            <Button variant={isScheduled ? "warning" : "success"} className="w-100 fw-bold text-white" onClick={handleBulkUpload} disabled={uploading}>
              {uploading ? "Processing..." : (isScheduled ? "SCHEDULE BULK BATCH" : "UPLOAD & SEND INSTANTLY")}
            </Button>
          </Card.Body>
        </Card>
      )}

      <ResultModal result={result} onClose={() => setResult(null)} />

      {/* Reusable Tracking Modal */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Status Report</Modal.Title></Modal.Header>
        <Modal.Body>
          {statusLoading ? <div className="text-center p-3"><Spinner animation="border" /></div> : (
            statusData ? <pre className="bg-light p-3 rounded">{JSON.stringify(statusData, null, 2)}</pre> : <p>No data.</p>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default TemplateInterface;